/**
 * GroupMembersPanel — Manage group chat members.
 * Shows list of participants and allows admins to add/remove/promote members.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { inviteParticipant, removeParticipant, changeParticipantRole, updateConversation } from '../../api/chat';
import type { ParticipantResponse } from '../../api/chat';
import type { SecurityUserResponse } from '../../api/security';
import { fetchAllSecurityUsers } from '../../api/security';
import { uploadDataUrl, fileUrl } from '../../api/fileStorage';
import CircleCropModal from '../CircleCropModal/CircleCropModal';
import UserAvatar from '../UserAvatar/UserAvatar';
import UserSearchInput from './UserSearchInput';
import styles from './Chat.module.css';

interface GroupMembersPanelProps {
  conversationId: string;
  conversationType: string;
  participants: ParticipantResponse[];
  currentUserGuid: string;
  isAdmin: boolean;
  /** DEV-1391 — current custom group avatar URL (null = derived). */
  currentAvatarUrl?: string | null;
  /** DEV-1391 — group name, for the avatar placeholder initial. */
  conversationName?: string | null;
  /** DEV-1391 — called after the avatar changes so the parent re-fetches. */
  onConversationUpdated?: () => void;
  onClose?: () => void;
}

export default function GroupMembersPanel({
  conversationId,
  conversationType,
  participants,
  currentUserGuid,
  isAdmin,
  currentAvatarUrl,
  conversationName,
  onConversationUpdated,
  onClose,
}: GroupMembersPanelProps) {
  const { t } = useAppTranslation('chat');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [operatingGuid, setOperatingGuid] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [allUsers, setAllUsers] = useState<SecurityUserResponse[]>([]);

  // DEV-1391 — group avatar editor state.
  const [avatarCrop, setAvatarCrop] = useState<{ src: string; w: number; h: number } | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  // Local participants state — syncs from prop but allows optimistic updates
  const [localParticipants, setLocalParticipants] = useState<ParticipantResponse[]>(participants);

  // Keep in sync when the parent prop changes (e.g. SignalR refresh)
  useEffect(() => {
    setLocalParticipants(participants);
  }, [participants]);

  // Load all platform users when "Add Member" is opened
  useEffect(() => {
    if (showAddMember && allUsers.length === 0) {
      fetchAllSecurityUsers()
        .then(setAllUsers)
        .catch((err) => console.error('[GroupMembersPanel] Failed to load users:', err));
    }
  }, [showAddMember, allUsers.length]);

  // Auto-clear errors after 5 seconds
  useEffect(() => {
    if (inviteError) {
      const timer = setTimeout(() => setInviteError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [inviteError]);

  const handleInviteMember = async (user: SecurityUserResponse) => {
    setInviteLoading(true);
    setInviteError(null);
    try {
      await inviteParticipant(conversationId, user.userGuid);
      // Optimistically add to local list so the UI updates immediately
      const newParticipant: ParticipantResponse = {
        userGuid: user.userGuid,
        displayName: user.displayName || user.name,
        screenName: user.screenName || null,
        profileImageUrl: user.profileImageUrl || '',
        role: 'member',
        status: 'active',
        joinedAt: new Date().toISOString(),
        isMuted: false,
      };
      setLocalParticipants((prev) => [...prev, newParticipant]);
      setShowAddMember(false);
    } catch (err: any) {
      console.error('[GroupMembersPanel] Failed to invite member:', err);
      const errorMsg = err?.message || 'Failed to add member';
      setInviteError(errorMsg);
    } finally {
      setInviteLoading(false);
    }
  };

  // DEV-1391 — group avatar: file picker → CircleCropModal → upload → persist.
  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) { setAvatarError(t('group.avatarTooLarge')); return; }
    if (!file.type.startsWith('image/')) { setAvatarError(t('group.avatarNotImage')); return; }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setAvatarCrop({ src: objectUrl, w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => { URL.revokeObjectURL(objectUrl); setAvatarError(t('group.avatarLoadFailed')); };
    img.src = objectUrl;
  };

  const handleAvatarCropConfirm = async (dataUrl: string) => {
    if (avatarCrop) URL.revokeObjectURL(avatarCrop.src);
    setAvatarCrop(null);
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      const ts = Date.now();
      const result = await uploadDataUrl(dataUrl, 'conversation-avatars', conversationId, `group-avatar-${ts}.png`);
      const url = `${fileUrl(result.key)}?v=${ts}`;
      await updateConversation(conversationId, { avatarUrl: url });
      onConversationUpdated?.();
    } catch (err: any) {
      console.error('[GroupMembersPanel] Avatar upload failed:', err);
      setAvatarError(err?.message || t('group.avatarUploadFailed'));
    } finally {
      setAvatarUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      await updateConversation(conversationId, { avatarUrl: '' });
      onConversationUpdated?.();
    } catch (err: any) {
      console.error('[GroupMembersPanel] Avatar remove failed:', err);
      setAvatarError(err?.message || t('group.avatarUploadFailed'));
    } finally {
      setAvatarUploading(false);
    }
  };

  // Don't show for direct conversations
  if (conversationType !== 'group') {
    return null;
  }

  const handleRemoveMember = async (userGuid: string) => {
    if (!window.confirm(t('confirm.removeMember') || 'Remove this member?')) {
      return;
    }

    setOperatingGuid(userGuid);
    try {
      await removeParticipant(conversationId, userGuid);
      // Optimistically remove from local list
      setLocalParticipants((prev) =>
        prev.filter((p) => p.userGuid.toUpperCase() !== userGuid.toUpperCase())
      );
    } catch (err) {
      console.error('Failed to remove member:', err);
      setInviteError('Failed to remove member');
    } finally {
      setOperatingGuid(null);
    }
  };

  const handleChangeRole = async (userGuid: string, newRole: 'admin' | 'member') => {
    setOperatingGuid(userGuid);
    console.log('[GroupMembersPanel] Changing role:', { conversationId, userGuid, newRole });
    try {
      await changeParticipantRole(conversationId, userGuid, newRole);
      console.log('[GroupMembersPanel] Role change successful');
      // Optimistically update local role
      setLocalParticipants((prev) =>
        prev.map((p) =>
          p.userGuid.toUpperCase() === userGuid.toUpperCase()
            ? { ...p, role: newRole }
            : p
        )
      );
    } catch (err: any) {
      console.error('[GroupMembersPanel] Failed to change role:', err);
      const errorMsg = err?.message || JSON.stringify(err) || 'Failed to change role';
      // Check for specific error patterns
      if (errorMsg.includes('minimum 2 admins') || errorMsg.includes('Minimum 2 admins')) {
        setInviteError('Cannot demote: Minimum 2 admins required');
      } else if (errorMsg.includes('403') || errorMsg.includes('Forbidden')) {
        setInviteError('You do not have permission to change roles');
      } else if (errorMsg.includes('400') || errorMsg.includes('BadRequest')) {
        setInviteError(`Error: ${errorMsg}`);
      } else if (errorMsg.includes('404') || errorMsg.includes('Not Found')) {
        setInviteError('Endpoint not found - backend may not be updated');
      } else if (errorMsg.includes('500')) {
        setInviteError('Server error - check backend logs');
      } else {
        setInviteError(`Failed to change role: ${errorMsg}`);
      }
    } finally {
      setOperatingGuid(null);
    }
  };

  const activeParticipants = localParticipants.filter(
    (p) => p.status === 'active' || p.status === 'invited'
  );

  return (
    <div
      style={{
        position: 'fixed',
        insetInlineEnd: 0,
        insetBlockStart: 'var(--header-height, 64px)',
        insetBlockEnd: 0,
        width: 300,
        display: 'flex',
        flexDirection: 'column',
        background: `var(--bg-elevated)`,
        borderInlineStart: `1px solid var(--border-default)`,
        zIndex: 1000,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBlockEnd: '1px solid var(--border-default)',
          background: `var(--bg-surface)`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
          Members ({activeParticipants.length})
        </div>
        {onClose && (
          <button
            onClick={onClose}
            title="Close members panel (ESC)"
            style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: 20,
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              minWidth: 32,
              minHeight: 32,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-surface)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* DEV-1391 — group avatar. Everyone sees it; admins can change/remove it. */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          padding: '14px 12px',
          borderBlockEnd: '1px solid var(--border-default)',
        }}
      >
        <div style={{ position: 'relative', width: 72, height: 72 }}>
          {currentAvatarUrl ? (
            <img
              src={currentAvatarUrl}
              alt=""
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid var(--border-default)',
              }}
            />
          ) : (
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--bg-tertiary)',
                color: 'var(--text-secondary)',
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              {(conversationName || '#').trim().charAt(0).toUpperCase()}
            </div>
          )}
          {avatarUploading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 11,
              }}
            >
              …
            </div>
          )}
        </div>

        {isAdmin && (
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={avatarFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarFileChange}
            />
            <button
              type="button"
              onClick={() => avatarFileInputRef.current?.click()}
              disabled={avatarUploading}
              style={{
                padding: '4px 10px',
                fontSize: 11,
                background: 'var(--bg-surface)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                cursor: avatarUploading ? 'not-allowed' : 'pointer',
                color: 'var(--text-primary)',
              }}
            >
              {currentAvatarUrl ? t('group.changePhoto') : t('group.addPhoto')}
            </button>
            {currentAvatarUrl && (
              <button
                type="button"
                onClick={handleRemoveAvatar}
                disabled={avatarUploading}
                style={{
                  padding: '4px 10px',
                  fontSize: 11,
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: avatarUploading ? 'not-allowed' : 'pointer',
                  color: 'var(--color-error)',
                }}
              >
                {t('group.removePhoto')}
              </button>
            )}
          </div>
        )}

        {avatarError && (
          <div style={{ fontSize: 11, color: 'var(--color-error)', textAlign: 'center' }}>
            {avatarError}
          </div>
        )}
      </div>

      {/* Add Member section (admin only) */}
      {isAdmin && (
        <div
          style={{
            padding: '8px 12px',
            borderBlockEnd: '1px solid var(--border-default)',
          }}
        >
          {showAddMember ? (
            <div>
              <UserSearchInput
                users={allUsers}
                excludeGuids={[
                  currentUserGuid,
                  ...localParticipants.map((p) => p.userGuid),
                ]}
                onSelect={handleInviteMember}
                placeholder="Search users to add..."
              />
              {inviteLoading && (
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBlockStart: 4 }}>
                  Adding member...
                </div>
              )}
              <button
                onClick={() => setShowAddMember(false)}
                style={{
                  marginBlockStart: 6,
                  padding: '4px 10px',
                  fontSize: 11,
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddMember(true)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 12,
                background: 'var(--bg-surface)',
                border: '1px dashed var(--border-default)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                transition: 'background 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-tertiary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--bg-surface)';
              }}
            >
              + Add Member
            </button>
          )}
        </div>
      )}

      {/* Members list */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
        }}
      >
        {activeParticipants.map((participant) => {
          const isCurrentUser = participant.userGuid.toUpperCase() === currentUserGuid.toUpperCase();
          const isOperating = operatingGuid === participant.userGuid;

          return (
            <div
              key={participant.userGuid}
              style={{
                padding: '8px',
                marginBlockEnd: '8px',
                borderRadius: 'var(--radius-md)',
                background: `var(--bg-tertiary)`,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: isOperating ? 0.6 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              {/* Avatar */}
              <UserAvatar
                userGuid={participant.userGuid}
                avatarUrl={participant.profileImageUrl}
                userName={participant.displayName}
                size="sm"
                showPresence
              />

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {participant.displayName || participant.screenName}
                  {isCurrentUser && (
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginInlineStart: 4 }}>
                      (you)
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: 'var(--text-tertiary)',
                  }}
                >
                  {participant.role === 'admin' ? '👑 Admin' : 'Member'}
                </div>
              </div>

              {/* Actions (admin only, not on self) */}
              {isAdmin && !isCurrentUser && (
                <div style={{ display: 'flex', gap: 4 }}>
                  {participant.role === 'member' ? (
                    <button
                      onClick={() => handleChangeRole(participant.userGuid, 'admin')}
                      disabled={isOperating}
                      title="Promote to admin"
                      style={{
                        padding: '4px 6px',
                        fontSize: 11,
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: isOperating ? 'not-allowed' : 'pointer',
                        color: 'var(--text-primary)',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isOperating) e.currentTarget.style.background = 'var(--bg-tertiary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-surface)';
                      }}
                    >
                      👑
                    </button>
                  ) : (
                    <button
                      onClick={() => handleChangeRole(participant.userGuid, 'member')}
                      disabled={isOperating}
                      title="Demote to member"
                      style={{
                        padding: '4px 6px',
                        fontSize: 11,
                        background: 'var(--bg-surface)',
                        border: '1px solid var(--border-default)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: isOperating ? 'not-allowed' : 'pointer',
                        color: 'var(--text-primary)',
                        transition: 'background 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (!isOperating) e.currentTarget.style.background = 'var(--bg-tertiary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-surface)';
                      }}
                    >
                      👤
                    </button>
                  )}

                  <button
                    onClick={() => handleRemoveMember(participant.userGuid)}
                    disabled={isOperating}
                    title="Remove member"
                    style={{
                      padding: '4px 6px',
                      fontSize: 11,
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: isOperating ? 'not-allowed' : 'pointer',
                      color: '#ef4444',
                      transition: 'background 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isOperating) e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-surface)';
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {inviteError && (
        <div
          style={{
            padding: '8px 12px',
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            fontSize: 12,
            borderBlockStart: '1px solid var(--border-default)',
          }}
        >
          {inviteError}
          <button
            onClick={() => setInviteError(null)}
            style={{
              marginInlineStart: 8,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#ef4444',
              fontSize: 12,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* DEV-1391 — avatar cropper (square/circle), reused from community logo. */}
      {avatarCrop && (
        <CircleCropModal
          imageSrc={avatarCrop.src}
          naturalW={avatarCrop.w}
          naturalH={avatarCrop.h}
          onConfirm={handleAvatarCropConfirm}
          onCancel={() => {
            if (avatarCrop) URL.revokeObjectURL(avatarCrop.src);
            setAvatarCrop(null);
          }}
        />
      )}
    </div>
  );
}
